import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Menu } from '@mui/material';
import {
  TextPrimaryBody2,
  Primary,
  PrimaryCol1,
  Body2,
  AttemptedBar,
  AmethystColor,
  Subtitle2,
  Caption,
  LinkButton,
  ImageContainer,
  ImageStyled,
  ImageImg,
  CheckIcon,
  ErrorIconStyled,
  ChipStyled,
  Badge,
  DotIconContainer,
  DotIcon,
  Badge2,
  Text,
  InfoIconContainer,
  InfoIcon,
  TabularChipContent,
  ChipIcon,
  DropdownIcon,
  ChipText,
  ChipSelectContainer,
  ChipSelectButton,
  ChipMenuPaper,
  ChipMenuList,
  ChipMenuTitle,
  ChipMenuItem,
  ChipMenuChip,
  DropdownIconWrapper,
  DropdownTrigger,
  FilterMenuItem,
  FilterMenuItemContent,
  FilterMenuItemText,
  FilterMenuItemSelectedText,
  FilterCheckIcon,
  FilterPopover
} from '../styles/TabularStyles';
import {
  Container,
  TextContainer,
  TitleWrapper,
  TitleText,
  TitleTextContainer,
  SectionsContainer,
  SectionsLabel,
  SectionChip,
  ChipContent,
  SectionNumber,
  EditActionIcon,
  EditActionIconButton,
  EditActionIconContainer,
  ContentTemplate as ContentTemplateRoot,
  DateColumn,
  ErrorInfoIcon
} from '../styles/TemplateStyles';
import Utils from '../../../common/utils';
import { ENTERKEY, TIMEFIELD_CONSTANTS } from '../../../common/constants';
import { getStatusStyles } from '../../../common/statusColorUtils';
import { getBrowserTimezone, formatDisplayTime, getTzLabelFromOptions } from '../../../common/dateTimeUtils';
import Icon from '../../icons/Icon';
import TooltipWrapper from '../../tooltip/TooltipWrapper';
import MathTextComponent from '../../common/MathTextComponent';
import InfoIconForError from '../../icons/InfoIconForError';

const { KEY_CODES } = TIMEFIELD_CONSTANTS;

const TextTemplate = (props) => {
  const { text, classes: customClasses = {} } = props;
  const Body2Component = customClasses?.body2Component || TextPrimaryBody2;

  if (customClasses?.body2) {
    return (
      <Body2Component className={customClasses.body2}>{text}</Body2Component>
    );
  }

  return (
    <Body2Component>{text}</Body2Component>
  );
};

TextTemplate.propTypes = {
  text: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    body2: PropTypes.string,
    body2Component: PropTypes.elementType
  })
};

TextTemplate.defaultProps = {
  classes: {}
};

const BadgeTemplate = (props) => {
  const {
    topics, status, label, showIcon = true, count, classes: customClasses = {}, components: customComponents = {}
  } = props;
  const StatusBadge = customComponents.badge || Badge;
  const TopicChip = customComponents.chip || ChipStyled;
  const badgeClassName = customComponents.badge
    ? (customClasses?.badge || '')
    : [customClasses?.badge, customClasses?.chip].filter(Boolean).join(' ');
  const hasCustomChipClass = Boolean(customClasses?.chip);

  const renderTopics = () => {
    if (topics) {
      const { backgroundColor, color } = getStatusStyles(status);

      return topics.map((topic, i) => (
        <TopicChip
          key={i}
          className={customClasses?.chip || undefined}
          label={
            count > 0 ? (
              <MathTextComponent
                component="p"
                text={`${topic} (${count})`}
                className={customClasses?.chipMathText}
              />
            ) : (
              <MathTextComponent
                component="p"
                text={topic}
                className={customClasses?.chipMathText}
              />
            )
          }
          size="small"
          style={{
            backgroundColor,
            borderRadius: 4,
            ...(hasCustomChipClass ? {} : { color })
          }}
        />
      ));
    }

    return null;
  };

  const renderStatus = () => {
    if (label) {
      const {
        backgroundColor,
        color
      } = getStatusStyles(status);

      return (
        <StatusBadge
          icon={showIcon ? (
            <DotIconContainer className={customClasses?.dotIconContainer || undefined}>
              <DotIcon name="DotIcon" style={{ color }} />
            </DotIconContainer>
          ) : null}
          label={label}
          size="small"
          variant="filled"
          className={badgeClassName || undefined}
          classes={customClasses?.badgeLabel ? { label: customClasses.badgeLabel } : undefined}
          sx={{
            backgroundColor: `${backgroundColor} !important`,
            color: `${color} !important`
          }}
          style={{ borderRadius: 4 }}
        />
      );
    }

    return null;
  };

  return (
    <>
      {renderTopics()}
      {renderStatus()}
    </>
  );
};
BadgeTemplate.defaultProps = {
  count: 0,
  classes: {},
  components: {}
};

BadgeTemplate.propTypes = {
  topics: PropTypes.arrayOf(PropTypes.string).isRequired,
  status: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  showIcon: PropTypes.string.isRequired,
  count: PropTypes.number,
  classes: PropTypes.shape({
    badge: PropTypes.string,
    badgeLabel: PropTypes.string,
    chip: PropTypes.string,
    chipMathText: PropTypes.string,
    dotIconContainer: PropTypes.string
  }),
  components: PropTypes.shape({
    badge: PropTypes.elementType,
    chip: PropTypes.elementType
  })
};

const ProgressTemplate = (props) => {
  const { attempted, total, attemptedPercent } = props;

  return (
    <Primary className="primaryGap">
      <Body2 variant="body2">{`${attempted}/${total}`}</Body2>
      <AttemptedBar
        variant="determinate"
        value={attemptedPercent}
      />
      <AmethystColor variant="body2">{`${attemptedPercent}%`}</AmethystColor>
    </Primary>
  );
};

ProgressTemplate.propTypes = {
  attempted: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  attemptedPercent: PropTypes.number.isRequired
};

const OverviewTemplate = (props) => {
  const {
    name, subtitle, status, onClick, imageSrc, initials, classes: customClasses
  } = props;

  const NameTypography = customClasses?.subtitle2Component || Subtitle2;

  const renderNameMathText = () => {
    if (customClasses?.subtitle2Component) {
      return (
        <MathTextComponent
          text={name}
          component={NameTypography}
          variant="subtitle2"
        />
      );
    }

    if (customClasses?.subtitle2) {
      return (
        <MathTextComponent
          className={customClasses.subtitle2}
          variant="subtitle2"
          text={name}
        />
      );
    }

    return (
      <MathTextComponent
        text={name}
        component={Subtitle2}
        variant="subtitle2"
      />
    );
  };

  const renderName = () => {
    const ImageWrapper = customClasses?.imageComponent || ImageStyled;

    if (imageSrc || initials) {
      return (
        <ImageContainer>
          {imageSrc
            ?
              <ImageImg src={imageSrc} alt={name} className={customClasses?.image} />
            :
              <ImageWrapper className={customClasses?.imageComponent ? undefined : customClasses?.image}><span>{initials}</span></ImageWrapper>
          }
          {renderNameMathText()}
        </ImageContainer>
      );
    }

    if (onClick) {
      return <LinkButton text={name} onClick={onClick} />;
    }

    return renderNameMathText();
  };

  return (
    <Primary>
      <PrimaryCol1 className={customClasses?.primaryCol1}>
        {renderName()}
        <MathTextComponent variant="caption" text={subtitle} component={Caption} />
      </PrimaryCol1>
      {status &&
      (
      <div>
        {status === 'success' && (
          <CheckIcon>
            <CheckCircleIcon />
          </CheckIcon>
        )}
        {status === 'warning' && (
          <ErrorIconStyled>
            <ErrorIcon />
          </ErrorIconStyled>
        )}
      </div>
      )}
    </Primary>
  );
};

OverviewTemplate.defaultProps = {
  status: null,
  onClick: null,
  imageSrc: null,
  initials: null,
  classes: null
};

OverviewTemplate.propTypes = {
  name: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  status: PropTypes.string,
  onClick: PropTypes.func,
  imageSrc: PropTypes.string,
  initials: PropTypes.string,
  classes: PropTypes.shape({
    subtitle2: PropTypes.string,
    subtitle2Component: PropTypes.elementType,
    primaryCol1: PropTypes.string,
    image: PropTypes.string,
    imageComponent: PropTypes.elementType
  })
};

const TextChipTemplate = (props) => {
  const {
    id,
    title,
    sections,
    label,
    onEditClick,
    showEditIcon,
    editIconTooltip,
    hasError,
    validationInfoToolTip,
    classes: customClasses = {},
    components: customComponents = {}
  } = props;

  const TextContainerComponent = customComponents.textContainer || TextContainer;
  const SectionsContainerComponent = customComponents.sectionsContainer || SectionsContainer;
  const SectionsLabelComponent = customComponents.sectionsLabel || SectionsLabel;
  const SectionChipComponent = customComponents.sectionChip || SectionChip;
  const SectionNumberComponent = customComponents.sectionNumber || SectionNumber;

  const handleEditClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEditClick(id);
  };

  return (
    <Container>
      <TextContainerComponent className={customComponents.textContainer ? undefined : customClasses?.textContainer}>
        <TitleWrapper>
          <TitleTextContainer>
            {title ? (
              <TitleText
                component="span"
                role="link"
                tabIndex={0}
                id={`select-${Utils.sanitizeString(title)}`}
                className={customClasses?.titleText}
                onKeyDown={(event) => {
                  if (event.key === ENTERKEY || event.key === KEY_CODES.SPACE) {
                    event.preventDefault();
                    event.currentTarget.click();
                  }
                }}
              >
                {title}
              </TitleText>
            ) : null}

            <EditActionIconContainer>
              {showEditIcon && (
                <TooltipWrapper tooltip={editIconTooltip} placement="top">
                  <EditActionIconButton
                    className={hasError ? 'editIconAlwaysVisible' : 'editActionIcon'}
                    onClick={handleEditClick}
                  >
                    <EditActionIcon name="EditActionIcon" />
                  </EditActionIconButton>
                </TooltipWrapper>
              )}
              {hasError && (
                <TooltipWrapper tooltip={validationInfoToolTip} placement="top">
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <ErrorInfoIcon>
                      <InfoIconForError />
                    </ErrorInfoIcon>
                  </span>
                </TooltipWrapper>
              )}
            </EditActionIconContainer>
          </TitleTextContainer>
        </TitleWrapper>
        <SectionsContainerComponent className={customComponents.sectionsContainer ? undefined : customClasses?.sectionsContainer}>
          {label ? <SectionsLabelComponent className={customComponents.sectionsLabel ? undefined : customClasses?.sectionsLabel}>{label}:</SectionsLabelComponent> : null}
          {sections ? sections.map((s, i) => (
            <SectionChipComponent className={customComponents.sectionChip ? undefined : customClasses?.sectionChip} key={i}>
              <ChipContent>
                <SectionNumberComponent className={customComponents.sectionNumber ? undefined : customClasses?.sectionNumber}>{s}</SectionNumberComponent>
              </ChipContent>
            </SectionChipComponent>
          )) : null}
        </SectionsContainerComponent>
      </TextContainerComponent>
    </Container>
  );
};

TextChipTemplate.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  sections: PropTypes.arrayOf(PropTypes.string).isRequired,
  label: PropTypes.string.isRequired,
  onEditClick: PropTypes.func,
  showEditIcon: PropTypes.bool,
  editIconTooltip: PropTypes.string,
  hasError: PropTypes.bool,
  validationInfoToolTip: PropTypes.string,
  classes: PropTypes.shape({
    textContainer: PropTypes.string,
    sectionsContainer: PropTypes.string,
    sectionsLabel: PropTypes.string,
    sectionChip: PropTypes.string,
    sectionNumber: PropTypes.string,
    titleText: PropTypes.string
  }),
  components: PropTypes.shape({
    textContainer: PropTypes.elementType,
    sectionsContainer: PropTypes.elementType,
    sectionsLabel: PropTypes.elementType,
    sectionChip: PropTypes.elementType,
    sectionNumber: PropTypes.elementType
  })
};
TextChipTemplate.defaultProps = {
  id: null,
  showEditIcon: false,
  onEditClick: () => {},
  editIconTooltip: null,
  hasError: false,
  validationInfoToolTip: null,
  classes: {},
  components: {}
};


const ContentTemplate = React.forwardRef(({ children }, ref) => (
  <ContentTemplateRoot ref={ref}>
    {children}
  </ContentTemplateRoot>
));

ContentTemplate.propTypes = {
  children: PropTypes.node.isRequired
};

const DateTemplate = ({ date, time, timeZoneOptions, info }) => {
  const currentTimeZoneLabel = getTzLabelFromOptions(getBrowserTimezone(), timeZoneOptions);
  const formattedDate = Utils.getDate(date);
  const formattedTime = formatDisplayTime(date, time);
  // do not display timezone alone if datetime not present due to data
  const displayTime = formattedTime && currentTimeZoneLabel
    ? `${formattedTime} ${currentTimeZoneLabel}`
    : formattedTime;

  return (
    <ContentTemplate>
      <DateColumn>
        <Info {...(info || {})} />
        <div>
          {!!formattedDate && (
            <SectionsLabel>{formattedDate}</SectionsLabel>
          )}
          {!!displayTime && <SectionsLabel>{displayTime}</SectionsLabel>}
        </div>
      </DateColumn>
    </ContentTemplate>
  );
};

DateTemplate.defaultProps = {
  time: null,
  timeZoneOptions: null,
  info: null
};

DateTemplate.propTypes = {
  date: PropTypes.string.isRequired,
  time: PropTypes.string,
  timeZoneOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    abbr: PropTypes.string,
    offset: PropTypes.string
  })),
  info: PropTypes.object
};

const ComprehensionTemplate = ({ label, type, bullet }) => {
  const normalized = type
    ?.toLowerCase()
    .replace(/\s*\d+\/\d+\s*|\s+/g, '')
    .trim();

  const { backgroundColor, color } = getStatusStyles(normalized);
  const appliedStyle = { backgroundColor, color };

  return (
    <Badge2 className={!bullet ? 'extraPadding' : ''} style={appliedStyle}>
      {bullet ?
        <DotIconContainer>
          <DotIcon name="DotIcon" style={{ color }} />
        </DotIconContainer> : null}
      <Text>{label}</Text>
    </Badge2>
  );
};

ComprehensionTemplate.defaultProps = {
  bullet: false
};

ComprehensionTemplate.propTypes = {
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  bullet: PropTypes.bool
};

const Info = (props) => {
  const {
    icon,
    tooltip,
    onClick,
    classes: classesProp = {},
    infoIconContainerClass: InfoIconContainerComponent
  } = props;
  const IconContainer = InfoIconContainerComponent || InfoIconContainer;
  const hasClickHandler = typeof onClick === 'function';

  if (!icon) {
    return null;
  }

  if (!hasClickHandler && !tooltip) {
    return null;
  }

  const hasCustomInfoIconSizing = Boolean(classesProp.infoIcon || classesProp.infoIconButton);
  const IconWrapperComponent = hasCustomInfoIconSizing ? 'span' : InfoIcon;
  const iconWrapperStyle = hasCustomInfoIconSizing
    ? {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      lineHeight: 0
    }
    : undefined;

  const clickHandlerProps = hasClickHandler
    ? {
      role: 'button',
      tabIndex: 0,
      onClick: (event) => {
        event.stopPropagation();
        onClick(event);
      },
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          onClick(event);
        }
      },
      'aria-label': tooltip || undefined
    }
    : {};

  const iconControl = (
    <IconWrapperComponent
      className={classesProp.infoIconButton || undefined}
      style={iconWrapperStyle}
      {...clickHandlerProps}
    >
      <Icon
        name={icon}
        className={classesProp.infoIcon}
      />
    </IconWrapperComponent>
  );

  const iconNode = (
    <IconContainer className={classesProp.infoIconContainer}>
      {iconControl}
    </IconContainer>
  );

  if (hasClickHandler) {
    return iconNode;
  }

  return (
    <TooltipWrapper
      tooltip={tooltip}
      placement="bottom-end"
    >
      {iconNode}
    </TooltipWrapper>
  );
};

Info.propTypes = {
  icon: PropTypes.string,
  tooltip: PropTypes.string,
  onClick: PropTypes.func,
  classes: PropTypes.shape({
    infoIcon: PropTypes.string,
    infoIconButton: PropTypes.string,
    infoIconContainer: PropTypes.string
  }),
  infoIconContainerClass: PropTypes.elementType
};

Info.defaultProps = {
  icon: null,
  tooltip: null,
  onClick: null,
  classes: {},
  infoIconContainerClass: null
};

const DropdownTemplate = (props) => {
  const triggerRef = useRef(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorPosition);

  const {
    value,
    options,
    onChange,
    disabled = false,
    variant = 'text',
    statusBased = false,
    iconPosition,
    menuTitle,
    chipStyle,
    menuChipStyle,
    classes: customClasses = {},
    components: customComponents = {}
  } = props;

  const BadgeComponent = customComponents.badge || Badge;
  const BadgeLabelComponent = customClasses.badgeLabelComponent;
  const FilterPopoverComponent = customClasses.filterPopoverComponent || FilterPopover;

  const handleOpen = (event) => {
    if (disabled) {
      return;
    }

    event.stopPropagation();

    const rect = triggerRef.current?.getBoundingClientRect();

    if (!rect) {
      return;
    }

    setAnchorPosition({
      top: Math.round(rect.bottom),
      left: Math.round(rect.left)
    });
  };

  const handleClose = () => {
    setAnchorPosition(null);
  };

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    handleClose();
  };

  const getLabelOfValue = (selectedValue) => {
    const selectedOption = options?.length > 0 ? options.find((opt) => opt.value === selectedValue) : null;
    if (selectedOption && selectedOption.label) {
      return selectedOption.label;
    }

    return selectedValue;
  };

  const getChipStyle = (chipValue, overrides) => {
    if (overrides) {
      return overrides;
    }

    if (!statusBased) {
      return {};
    }

    const { backgroundColor, color } = getStatusStyles(chipValue?.toLowerCase());

    return { backgroundColor, color };
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuSelect = (event, option) => {
    event.stopPropagation();
    setAnchorEl(null);

    if (option.value !== value) {
      onChange(option.value);
    }
  };

  /**
   * "Change to:" picker — a labelled list of status chips instead of a plain
   * option list. Opted into by passing `menuTitle` alongside the chip variant.
   */
  const renderChipMenu = () => (
    <Primary>
      <ChipSelectButton
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={anchorEl ? true : undefined}
        aria-label={`Change ${getLabelOfValue(value)}`}
        onClick={handleMenuOpen}
      >
        <BadgeComponent
          label={getLabelOfValue(value)}
          size="small"
          tabIndex={-1}
          style={{ borderRadius: 4, ...getChipStyle(value, chipStyle) }}
        />

        <DropdownIconWrapper>
          <DropdownIcon className={anchorEl ? 'rotateIcon' : ''}>
            <ArrowDropDownIcon />
          </DropdownIcon>
        </DropdownIconWrapper>
      </ChipSelectButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        disableAutoFocusItem
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slots={{ list: ChipMenuList }}
        slotProps={{
          paper: {
            component: ChipMenuPaper,
            elevation: 0
          },
          list: {
            disablePadding: true
          }
        }}
      >
        <ChipMenuTitle disabled>
          {menuTitle}
        </ChipMenuTitle>

        {options.filter((opt) => !opt.hidden).map((opt) => (
          <ChipMenuItem
            key={opt.value}
            disableRipple
            selected={opt.value === value}
            onClick={(event) => handleMenuSelect(event, opt)}
          >
            <ChipMenuChip style={getChipStyle(opt.value, menuChipStyle)}>
              {opt.label}
            </ChipMenuChip>
          </ChipMenuItem>
        ))}
      </Menu>
    </Primary>
  );

  const renderChipTrigger = () => {
    let style = {};

    if (statusBased) {
      const { backgroundColor, color } =
        getStatusStyles(value?.toLowerCase());
      style = { backgroundColor, color };
    }

    if (iconPosition === 'inside') {
      return (
        <BadgeComponent
          label={(
            <TabularChipContent>
              <ChipText>{getLabelOfValue(value)}</ChipText>

              <ChipIcon className={open ? 'rotateIcon' : ''}>
                <ArrowDropDownIcon />
              </ChipIcon>
            </TabularChipContent>
          )}
          size="small"
          style={{
            borderRadius: 4,
            ...style
          }}
        />
      );
    }

    return (
      <ChipSelectContainer>
        <BadgeComponent
          label={BadgeLabelComponent ? (
            <BadgeLabelComponent component="span">
              {getLabelOfValue(value)}
            </BadgeLabelComponent>
          ) : getLabelOfValue(value)}
          size="small"
          style={{
            borderRadius: 4,
            ...style
          }}
        />

        <DropdownIconWrapper>
          <DropdownIcon className={open ? 'rotateIcon' : ''}>
            <ArrowDropDownIcon />
          </DropdownIcon>
        </DropdownIconWrapper>
      </ChipSelectContainer>
    );
  };

  const renderTrigger = () => {
    if (variant === 'chip') {
      return renderChipTrigger();
    }

    return (
      <Body2>
        {value}
      </Body2>
    );
  };

  if (variant === 'chip' && menuTitle) {
    return renderChipMenu();
  }

  return (
    <>
      <Primary onClick={(event) => event.stopPropagation()}>
        <DropdownTrigger
          ref={triggerRef}
          onClick={handleOpen}
          aria-haspopup="listbox"
          aria-expanded={open ? 'true' : 'false'}
          data-disabled={disabled ? 'true' : 'false'}
        >
          {renderTrigger()}
        </DropdownTrigger>
      </Primary>

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        slotProps={{
          paper: {
            component: FilterPopoverComponent,
            elevation: 0
          },
          list: {
            disablePadding: true
          }
        }}
      >
        {options.filter((opt) => !opt.hidden).map((opt) => {
          const selected = value === opt.value;

          return (
            <FilterMenuItem
              key={opt.value}
              selected={selected}
              onClick={(event) => {
                event.stopPropagation();
                handleSelect(opt.value);
              }}
            >
              <FilterMenuItemContent>
                {selected ? (
                  <FilterMenuItemSelectedText>
                    {opt.label}
                  </FilterMenuItemSelectedText>
                ) : (
                  <FilterMenuItemText>
                    {opt.label}
                  </FilterMenuItemText>
                )}

                {selected && (
                  <FilterCheckIcon>
                    <Icon name="TickIcon" />
                  </FilterCheckIcon>
                )}
              </FilterMenuItemContent>
            </FilterMenuItem>
          );
        })}
      </Menu>
    </>
  );
};

DropdownTemplate.propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      hidden: PropTypes.bool
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  statusBased: PropTypes.bool,
  iconPosition: PropTypes.string,
  menuTitle: PropTypes.string,
  chipStyle: PropTypes.object,
  menuChipStyle: PropTypes.object,
  classes: PropTypes.shape({
    badgeLabelComponent: PropTypes.elementType,
    filterPopoverComponent: PropTypes.elementType
  }),
  components: PropTypes.shape({
    badge: PropTypes.elementType
  })
};

DropdownTemplate.defaultProps = {
  value: '',
  disabled: false,
  variant: 'text',
  statusBased: false,
  iconPosition: 'outside',
  menuTitle: null,
  chipStyle: null,
  menuChipStyle: null,
  classes: {},
  components: {}
};

export default DropdownTemplate;

export {
  OverviewTemplate,
  TextTemplate,
  TextChipTemplate,
  BadgeTemplate,
  ProgressTemplate,
  ContentTemplate,
  DateTemplate,
  ComprehensionTemplate,
  Info,
  DropdownTemplate
};
